/**
 * SidebarBadgesContext
 *
 * Polls the small set of "unread / pending" counters that drive sidebar
 * badges (Unified Inbox, WhatsApp, Bid Requests). Centralised so each
 * sidebar render doesn't fan out into independent fetchers.
 *
 * Polling cadence: 60s (see POLL_INTERVALS.sidebarBadges). Pauses when the
 * tab is hidden via the page-visibility-aware usePolling hook.
 *
 * Each fetch is best-effort: if the endpoint fails we keep the previous
 * count so a transient error doesn't make a "0" badge flicker.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';
import AuthContext from './AuthContext';
import { usePluginContext } from './PluginContext';
import API_BASE_URL from '../config/api';
import { devLog } from '../utils/devLog';
import { usePolling, POLL_INTERVALS } from '../hooks/usePolling';

const SidebarBadgesContext = createContext({
  unifiedOrders: 0,
  whatsapp: 0,
  bidRequests: 0,
  refresh: () => Promise.resolve(),
});

export const SidebarBadgesProvider = ({ children }) => {
  const { authToken } = useContext(AuthContext);
  const { isPluginEnabled } = usePluginContext();
  const accessToken = authToken?.access;

  const [counts, setCounts] = useState({
    unifiedOrders: 0,
    whatsapp: 0,
    bidRequests: 0,
  });

  const setIfChanged = useCallback((key, value) => {
    setCounts((prev) => (prev[key] === value ? prev : { ...prev, [key]: value }));
  }, []);

  const fetchAll = useCallback(async () => {
    if (!accessToken) return;
    const headers = { Authorization: `Bearer ${accessToken}` };

    // Unified Inbox — pending/new orders. Use status=new + page_size=1
    // so we just yank the pagination `count`.
    if (isPluginEnabled('unified_inbox')) {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/unified-orders/`, {
          params: { status: 'new', page_size: 1 },
          headers,
        });
        // Backend may return either {count, results} or a bare list — handle both
        const count =
          typeof res.data?.count === 'number'
            ? res.data.count
            : Array.isArray(res.data)
              ? res.data.length
              : Array.isArray(res.data?.results)
                ? res.data.results.length
                : 0;
        setIfChanged('unifiedOrders', count);
      } catch (err) {
        // 401 is handled globally; everything else just stays at the
        // previous value so the badge doesn't flap to 0 on a hiccup.
        if (err?.response?.status !== 401) {
          devLog.warn('Sidebar badge: unified-orders fetch failed', err?.message);
        }
      }
    }

    // WhatsApp — count threads with unread_count > 0. We try the
    // unread_only filter first (cheap if backend supports it), fall back
    // to client-side count.
    if (isPluginEnabled('whatsapp')) {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/whatsapp/threads/`, {
          params: { unread_only: 1 },
          headers,
        });
        const rows = Array.isArray(res.data)
          ? res.data
          : res.data?.results || res.data?.threads || [];
        // Whether or not the backend filtered, we can compute unread count
        // by summing unread_count > 0. If unread_only worked, length === count.
        const count = rows.filter((t) => Number(t.unread_count || 0) > 0).length;
        setIfChanged('whatsapp', count);
      } catch (err) {
        if (err?.response?.status !== 401) {
          devLog.warn('Sidebar badge: whatsapp threads fetch failed', err?.message);
        }
      }
    }

    // Bid requests — pending bids. Backend returns {bids: [...]} where each
    // row has is_accepted and is_declined booleans.
    if (isPluginEnabled('bid_requests')) {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/bidding_orders/`, {
          headers,
        });
        const list = Array.isArray(res.data?.bids)
          ? res.data.bids
          : Array.isArray(res.data)
            ? res.data
            : res.data?.results || [];
        const count = list.filter(
          (b) => !b.is_accepted && !b.is_declined
        ).length;
        setIfChanged('bidRequests', count);
      } catch (err) {
        if (err?.response?.status !== 401) {
          devLog.warn('Sidebar badge: bid-requests fetch failed', err?.message);
        }
      }
    }
  }, [accessToken, isPluginEnabled, setIfChanged]);

  // Poll on the slow cadence. usePolling fires once on mount and pauses
  // when the tab is hidden, so the sidebar badges stay current without
  // hammering the backend.
  usePolling(
    fetchAll,
    accessToken ? POLL_INTERVALS.sidebarBadges : 0,
    [accessToken]
  );

  const value = useMemo(
    () => ({
      ...counts,
      refresh: fetchAll,
    }),
    [counts, fetchAll]
  );

  return (
    <SidebarBadgesContext.Provider value={value}>
      {children}
    </SidebarBadgesContext.Provider>
  );
};

export const useSidebarBadges = () => useContext(SidebarBadgesContext);

export default SidebarBadgesContext;
